/**
 * 2.0 wave 2, W2-CHROME (spec 7.5) — `useAccountView`: the account's VIEW
 * (Training, Coaching or Guardian), the one fact the tab bar, the desktop rail,
 * the landing tab and Settings all read (B392).
 *
 * ## The contract (names fixed by the spec; other lanes import them)
 *
 *   { view, options, setView, ready }
 *
 *  · `options` are derived only from the account's roles, always in the order
 *    Training, Coaching, Guardian: training if the server role fact includes
 *    the athlete side, coaching if it includes the coach side, guardian if the
 *    account guards someone. The role fact is `useAccountRole` (server truth,
 *    degrading to the pre-migration signal exactly as the boundary does).
 *  · `view` is the stored choice when it is one of the options AND the
 *    account's facts have settled, else the first option. Until `ready`, the
 *    stored value is not trusted, so a view the account does not have is never
 *    painted, even for the length of a load.
 *  · `setView` writes the choice for this user on this device. A value outside
 *    `options` is refused. Nothing here fetches and nothing changes what the
 *    server authorizes (CH-11): the view only picks which presentation the
 *    client shows.
 *
 * ## Flag off
 *
 * With `settings_2_0_v1` off the hook returns the LEGACY derivation
 * (guardian-only, then coach-only, else training) with `options` of length 1
 * and a `setView` that does nothing. The chrome components do not even mount
 * this hook with the flag off (they fork on the flag one component above), so
 * the legacy render paths and their requests are untouched.
 *
 * The flag id is a string literal on purpose: the config export rake derives
 * its required set from literal call sites.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import {
  accountViewOptions,
  parseStoredAccountView,
  readAccountViewRaw,
  resolveAccountView,
  subscribeAccountView,
  writeStoredAccountView,
  type AccountViewId,
} from '../lib/accountView';
import { useAccountRole } from './useAccountRole';
import { useCoachMode } from './useCoachMode';
import { useGuardianMode } from './useGuardianMode';
import { useServerFlag } from './useServerFlag';

export interface AccountViewState {
  view: AccountViewId;
  options: AccountViewId[];
  setView: (view: AccountViewId) => void;
  /** The role fact and the guardian check have settled: `view` is final. */
  ready: boolean;
}

export function useAccountView(): AccountViewState {
  const settingsOn = useServerFlag('settings_2_0_v1');
  const { user } = useAuthContext();
  const { guardianOnly, isGuardian, loading: guardianLoading } = useGuardianMode();
  const { coachOnly: legacyCoachOnly } = useCoachMode();
  const { coachOnly, hasAthleteSide, hasCoachSide, boundaryPending } = useAccountRole();
  const rawStored = useSyncExternalStore(subscribeAccountView, readAccountViewRaw, readAccountViewRaw);
  const uid = user?.id ?? null;

  const ready = !boundaryPending && !guardianLoading;

  // The pre-2.0 derivation, kept verbatim: BottomTab's own precedence.
  const legacyView: AccountViewId = guardianOnly
    ? 'guardian'
    : legacyCoachOnly
      ? 'coaching'
      : 'training';

  // A guardian-only account has no athlete side even though the role fact
  // defaults to "athlete" for it; a coach-only account's fact may default the
  // same way during the migration window, which `coachOnly` already vetoes.
  const derivedKey = settingsOn
    ? accountViewOptions({
        athleteSide: hasAthleteSide && !coachOnly && !guardianOnly,
        coachSide: hasCoachSide || coachOnly,
        guardianSide: isGuardian || guardianOnly,
      }).join('|')
    : '';
  // A stable array identity: the ids joined are the content, so consumers that
  // put `options` in an effect's deps do not re-run on every render.
  const options = useMemo<AccountViewId[]>(
    () => (derivedKey ? (derivedKey.split('|') as AccountViewId[]) : [legacyView]),
    [derivedKey, legacyView],
  );

  const stored = ready && settingsOn ? parseStoredAccountView(rawStored, uid) : null;
  const view = settingsOn ? resolveAccountView(options, stored) : legacyView;

  const setView = useCallback(
    (next: AccountViewId): void => {
      if (!settingsOn || !uid) return;
      if (!options.includes(next)) return;
      writeStoredAccountView(uid, next);
    },
    [settingsOn, uid, options],
  );

  return { view, options, setView, ready };
}
